import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, clearAuthToken } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Shield, Car, AlertTriangle, LogOut, CheckCircle, Bell, X } from 'lucide-react';
import { GUARD_POLLING_INTERVAL_MS } from '@/constants';

const GuardDashboard = () => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState({ available_spots: 0, occupied_spots: 0, spots: [], active_bookings: [] });
  const [overstay, setOverstay] = useState({ overstay_count: 0, bookings: [] });
  const [lastCompletedBooking, setLastCompletedBooking] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [checkInData, setCheckInData] = useState({
    vehicle_number: '',
    visitor_phone: '',
    visitor_name: '',
    flat_visiting: '',
    estimated_hours: 1
  });
  const [loading, setLoading] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [otpStep, setOtpStep] = useState(false);
  const [visitorOtp, setVisitorOtp] = useState('');
  const [sentOtp, setSentOtp] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [inventoryRes, overstayRes, alertsRes] = await Promise.all([
        axios.get(`${API}/guard/inventory`),
        axios.get(`${API}/guard/overstay`),
        axios.get(`${API}/guard/alerts`)
      ]);

      setInventory(inventoryRes.data);
      setOverstay(overstayRes.data);
      setAlerts(alertsRes.data.alerts || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    }
  }, []);

  useEffect(() => {
    const role = localStorage.getItem('role');
    if (!role || role !== 'guard') {
      navigate('/auth/guard');
      return;
    }
    fetchData();
    const interval = setInterval(fetchData, GUARD_POLLING_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [navigate, fetchData]);

  const handleCheckIn = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      
      // Check available spots
      if (inventory.available_spots === 0) {
        toast.error('No available parking spots');
        setLoading(false);
        return;
      }
      
      // Send OTP to visitor
      const res = await axios.post(
        `${API}/guard/send-visitor-otp`,
        checkInData,
        {}
      );

      setSentOtp(res.data.otp);
      setOtpStep(true);
      toast.success(`OTP sent to visitor: ${res.data.otp}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setLoading(true);

    try {
      
      const res = await axios.post(
        `${API}/guard/verify-visitor-otp`,
        { ...checkInData, otp: visitorOtp },
        {}
      );

      setQrCode(res.data.qr_code);
      setCurrentBooking(res.data.booking);
      setOtpStep(false);
      
      if (res.data.is_free) {
        // Free spot - skip payment, go directly to completion
        const passLink = `${window.location.origin}/visitor/${res.data.booking.id}`;
        setLastCompletedBooking({
          id: res.data.booking.id,
          spot_number: res.data.booking.spot_number,
          vehicle_number: res.data.booking.vehicle_number,
          pass_link: passLink
        });
        toast.success('OTP verified! FREE spot allocated. Check-in complete.');
        setQrCode(null);
        setCurrentBooking(null);
        setCheckInData({ vehicle_number: '', visitor_phone: '', visitor_name: '', flat_visiting: '', estimated_hours: 1 });
        setVisitorOtp('');
      } else {
        toast.success('OTP verified! Payment QR generated');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentConfirm = async () => {
    if (!currentBooking) return;
    
    try {
      
      await axios.post(
        `${API}/guard/confirm-payment?booking_id=${currentBooking.id}`,
        {},
        {}
      );
      
      // Show visitor pass link in a persistent state
      const passLink = `${window.location.origin}/visitor/${currentBooking.id}`;
      setLastCompletedBooking({
        id: currentBooking.id,
        spot_number: currentBooking.spot_number,
        vehicle_number: currentBooking.vehicle_number,
        pass_link: passLink
      });
      
      toast.success('Payment confirmed! Check-in complete.');
      
      // Reset form
      setQrCode(null);
      setCurrentBooking(null);
      setCheckInData({
        vehicle_number: '',
        visitor_phone: '',
        visitor_name: '',
        flat_visiting: '',
        estimated_hours: 1
      });
      setVisitorOtp('');
      setSentOtp(null);
      
      fetchData();
    } catch (error) {
      toast.error('Payment confirmation failed');
    }
  };

  const handleCheckOut = async (bookingId) => {
    
    try {
      await axios.post(
        `${API}/guard/checkout`,
        { booking_id: bookingId },
        {}
      );

      toast.success('Vehicle checked out successfully');
      setQrCode(null);
      setCurrentBooking(null);
      setCheckInData({
        vehicle_number: '',
        visitor_phone: '',
        visitor_name: '',
        flat_visiting: '',
        spot_id: '',
        estimated_hours: 1
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-out failed');
    }
  };

  const handleLogout = () => {
    axios.post(`${API}/auth/logout`).catch(() => {});
    clearAuthToken();
    localStorage.clear();
    navigate('/');
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F4F4F5' }}>
      {/* Header */}
      <div className="border-b" style={{ backgroundColor: '#09090B', borderColor: '#09090B' }}>
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div style={{ backgroundColor: '#FBBF24', padding: '12px' }}>
              <Shield size={28} style={{ color: '#09090B' }} />
            </div>
            <div>
              <h1 
                className="text-2xl sm:text-3xl tracking-tight font-bold"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#FFFFFF' }}
                data-testid="guard-dashboard-title"
              >
                Security Dashboard
              </h1>
              <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#F4F4F5', fontSize: '14px' }}>
                Real-time parking management
              </p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            data-testid="logout-button"
            style={{ backgroundColor: '#FFFFFF', color: '#09090B', fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div 
            className="p-6 border"
            style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7', borderWidth: '2px' }}
            data-testid="available-spots-card"
          >
            <p className="text-xs tracking-[0.2em] uppercase font-bold mb-2" style={{ color: '#71717A' }}>
              Available Spots
            </p>
            <p 
              className="text-5xl font-black"
              style={{ fontFamily: 'JetBrains Mono, monospace', color: '#10B981' }}
            >
              {inventory.available_spots}
            </p>
          </div>

          <div 
            className="p-6 border"
            style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7', borderWidth: '2px' }}
            data-testid="occupied-spots-card"
          >
            <p className="text-xs tracking-[0.2em] uppercase font-bold mb-2" style={{ color: '#71717A' }}>
              Occupied
            </p>
            <p 
              className="text-5xl font-black"
              style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}
            >
              {inventory.occupied_spots}
            </p>
          </div>

          <div 
            className="p-6 border"
            style={{ 
              backgroundColor: overstay.overstay_count > 0 ? '#DC2626' : '#FFFFFF', 
              borderColor: '#E4E4E7', 
              borderWidth: '2px' 
            }}
            data-testid="overstay-card"
          >
            <p 
              className="text-xs tracking-[0.2em] uppercase font-bold mb-2" 
              style={{ color: overstay.overstay_count > 0 ? '#FFFFFF' : '#71717A' }}
            >
              Overstay Alerts
            </p>
            <p 
              className="text-5xl font-black"
              style={{ 
                fontFamily: 'JetBrains Mono, monospace', 
                color: overstay.overstay_count > 0 ? '#FFFFFF' : '#09090B' 
              }}
            >
              {overstay.overstay_count}
            </p>
          </div>
        </div>

        {/* Check-In Form */}
        <div 
          className="p-6 border mb-8"
          style={{ backgroundColor: '#FFFFFF', borderColor: '#09090B', borderWidth: '2px' }}
        >
          <h2 
            className="text-xl font-semibold mb-4"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}
          >
            Vehicle Check-In
          </h2>
          <form onSubmit={handleCheckIn} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Vehicle Number *</Label>
              <Input
                placeholder="MH-01-AB-1234"
                value={checkInData.vehicle_number}
                onChange={(e) => setCheckInData({ ...checkInData, vehicle_number: e.target.value.toUpperCase() })}
                required
                data-testid="vehicle-number-input"
                className="mt-2 p-4 border-2 focus:border-black"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              />
            </div>
            <div>
              <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Visitor Phone *</Label>
              <Input
                placeholder="9876543210"
                value={checkInData.visitor_phone}
                onChange={(e) => setCheckInData({ ...checkInData, visitor_phone: e.target.value })}
                required
                data-testid="visitor-phone-input"
                className="mt-2 p-4 border-2 focus:border-black"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              />
            </div>
            <div>
              <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Visitor Name</Label>
              <Input
                placeholder="John Doe"
                value={checkInData.visitor_name}
                onChange={(e) => setCheckInData({ ...checkInData, visitor_name: e.target.value })}
                data-testid="visitor-name-input"
                className="mt-2 p-4 border-2 focus:border-black"
              />
            </div>
            <div>
              <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Flat Visiting *</Label>
              <Input
                placeholder="A-101"
                value={checkInData.flat_visiting}
                onChange={(e) => setCheckInData({ ...checkInData, flat_visiting: e.target.value })}
                required
                data-testid="flat-visiting-input"
                className="mt-2 p-4 border-2 focus:border-black"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              />
            </div>
            <div>
              <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Estimated Duration (hours) *</Label>
              <Input
                type="number"
                min="15"
                step="15"
                value={checkInData.estimated_hours}
                onChange={(e) => setCheckInData({ ...checkInData, estimated_hours: parseFloat(e.target.value) })}
                required
                data-testid="estimated-hours-input"
                className="mt-2 p-4 border-2 focus:border-black"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
                placeholder="e.g. 30, 60, 120"
              />
            </div>
            
            <div className="md:col-span-2 lg:col-span-3 p-4" style={{ backgroundColor: '#FBBF24' }}>
              <p className="text-xs tracking-[0.2em] uppercase font-bold mb-1" style={{ color: '#09090B' }}>
                Available Spots
              </p>
              <p className="text-3xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>
                {inventory.available_spots} Spots Ready
              </p>
              <p className="text-xs mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
                {inventory.spots?.some(s => s.is_free) ? 'Includes FREE spots available' : 'System will auto-allocate spot after payment'}
              </p>
            </div>
            
            <div className="md:col-span-2 lg:col-span-3">
              <Button
                type="submit"
                disabled={loading || inventory.available_spots === 0}
                data-testid="checkin-submit-button"
                className="w-full p-4 transition-transform hover:-translate-y-1"
                style={{
                  backgroundColor: '#09090B',
                  color: '#FFFFFF',
                  fontFamily: 'Cabinet Grotesk, sans-serif',
                  fontWeight: 'bold'
                }}
              >
                {loading ? 'Processing...' : 'Send OTP to Visitor'}
              </Button>
            </div>
          </form>
        </div>

        {/* OTP Verification Step */}
        {otpStep && sentOtp && (
          <div 
            className="p-6 border mb-8"
            style={{ 
              backgroundColor: '#FBBF24',
              borderColor: '#09090B',
              borderWidth: '2px'
            }}
            data-testid="otp-verification-step"
          >
            <h2 
              className="text-xl font-semibold mb-4 text-center"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}
            >
              Visitor OTP Verification
            </h2>
            
            <div className="bg-white p-6 mb-4">
              <p className="text-sm mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                OTP sent to: <span className="font-bold" style={{ color: '#09090B' }}>{checkInData.visitor_phone}</span>
              </p>
              <p 
                className="text-3xl font-black mb-4 text-center"
                style={{ fontFamily: 'JetBrains Mono, monospace', color: '#10B981' }}
                data-testid="displayed-otp"
              >
                OTP: {sentOtp}
              </p>
              <p className="text-xs text-center" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#DC2626' }}>
                (Mock OTP - In production, visitor receives via SMS)
              </p>
            </div>
            
            <div className="bg-white p-6">
              <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
                Enter OTP from Visitor
              </Label>
              <Input
                type="text"
                placeholder="1234"
                value={visitorOtp}
                onChange={(e) => setVisitorOtp(e.target.value)}
                data-testid="visitor-otp-input"
                className="mt-2 p-4 border-2 focus:border-black mb-4"
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '24px', textAlign: 'center' }}
                maxLength={4}
              />
              
              <div className="flex gap-2">
                <Button
                  onClick={handleVerifyOtp}
                  disabled={loading || visitorOtp.length !== 4}
                  data-testid="verify-otp-button"
                  className="flex-1 p-4"
                  style={{
                    backgroundColor: '#10B981',
                    color: '#FFFFFF',
                    fontFamily: 'Cabinet Grotesk, sans-serif',
                    fontWeight: 'bold'
                  }}
                >
                  {loading ? 'Verifying...' : 'Verify OTP & Generate Payment QR'}
                </Button>
                <Button
                  onClick={() => {
                    setOtpStep(false);
                    setVisitorOtp('');
                    setSentOtp(null);
                  }}
                  variant="outline"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Payment QR & Spot Allocation */}
        {qrCode && currentBooking && (
          <div 
            className="p-6 border mb-8"
            style={{ 
              backgroundColor: '#FBBF24',
              borderColor: '#09090B',
              borderWidth: '2px'
            }}
            data-testid="qr-code-display"
          >
            <h2 
              className="text-xl font-semibold mb-4 text-center"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}
            >
              Payment & Spot Allocation
            </h2>
            
            <div className="bg-white p-4 mb-4 text-center">
              <p className="text-xs tracking-[0.2em] uppercase font-bold mb-2" style={{ color: '#71717A' }}>
                COLLECT PAYMENT
              </p>
              <img 
                src={qrCode} 
                alt="Payment QR" 
                className="mx-auto mb-4" 
                style={{ maxWidth: '300px', width: '100%' }} 
              />
              <p 
                className="text-2xl font-black mb-2"
                style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}
              >
                Amount: ₹{currentBooking.amount}
              </p>
              <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                Vehicle: {currentBooking.vehicle_number}
              </p>
            </div>
            
            <div className="bg-white p-6">
              <p className="text-sm mb-4 text-center" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                Parking spot will be revealed after payment confirmation
              </p>
              
              <Button
                onClick={handlePaymentConfirm}
                data-testid="complete-checkin-button"
                className="p-4 w-full transition-transform hover:-translate-y-1"
                style={{
                  backgroundColor: '#10B981',
                  color: '#FFFFFF',
                  fontFamily: 'Cabinet Grotesk, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '18px'
                }}
              >
                Payment Received - Complete Check-In
              </Button>
              <p className="text-xs mt-2 text-center" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                Visitor pass & parking spot will be shared after confirming payment
              </p>
            </div>
          </div>
        )}

        {/* Urgent Alerts from Owner Cancellations */}
        {alerts.length > 0 && (
          <div className="space-y-3 mb-8">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className="p-4 border-2 flex items-start justify-between gap-4"
                style={{ backgroundColor: '#FEF2F2', borderColor: '#DC2626' }}
                data-testid={`guard-alert-${alert.id}`}
              >
                <div className="flex items-start gap-3">
                  <Bell size={20} style={{ color: '#DC2626', marginTop: '2px', flexShrink: 0 }} />
                  <div>
                    <p className="text-sm font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#991B1B' }}>
                      Urgent Alert
                    </p>
                    <p className="text-sm mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#7F1D1D', whiteSpace: 'pre-line' }}>
                      {alert.message}
                    </p>
                    <p className="text-xs mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#A1A1AA' }}>
                      {new Date(alert.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    await axios.post(`${API}/guard/alerts/${alert.id}/dismiss`, {});
                    setAlerts(alerts.filter(a => a.id !== alert.id));
                  }}
                  data-testid={`dismiss-alert-${alert.id}`}
                  style={{ borderColor: '#DC2626', color: '#DC2626', flexShrink: 0 }}
                >
                  <X size={14} />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Check-In Success + Visitor Pass Link */}
        {lastCompletedBooking && (
          <div 
            className="p-6 border mb-8"
            style={{ 
              backgroundColor: '#D1FAE5',
              borderColor: '#10B981',
              borderWidth: '2px'
            }}
            data-testid="checkin-success-banner"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#065F46' }}>
                Check-In Complete
              </h2>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setLastCompletedBooking(null)}
                style={{ borderColor: '#10B981', color: '#065F46' }}
              >
                Dismiss
              </Button>
            </div>
            <div className="bg-white p-4 mb-3">
              <p 
                className="text-5xl font-black mb-3 text-center"
                style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}
                data-testid="allocated-spot-number"
              >
                {lastCompletedBooking.spot_number}
              </p>
              <p className="text-sm text-center" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
                Assigned to <strong style={{ fontFamily: 'JetBrains Mono, monospace' }}>{lastCompletedBooking.vehicle_number}</strong>
              </p>
              <p className="text-sm mt-2 text-center font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#DC2626' }}>
                Apply red sticker to windshield
              </p>
            </div>
            <div className="p-4 border-2" style={{ backgroundColor: '#F4F4F5', borderColor: '#10B981' }}>
              <p className="text-xs font-bold mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
                Visitor Digital Pass Link:
              </p>
              <a 
                href={lastCompletedBooking.pass_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-bold block"
                style={{ fontFamily: 'JetBrains Mono, monospace', color: '#10B981', wordBreak: 'break-all' }}
                data-testid="visitor-pass-link"
              >
                {lastCompletedBooking.pass_link}
              </a>
              <p className="text-xs mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                Share this link with the visitor
              </p>
            </div>
          </div>
        )}
        {overstay.overstay_count > 0 && (
          <div 
            className="p-6 border mb-8"
            style={{ 
              backgroundColor: '#FFFFFF',
              borderColor: '#DC2626',
              borderWidth: '2px',
              backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/b70eba2e-88eb-4f5d-9ebc-ce80d62e76ef/images/e723c69782c5ab7850909b519b3458f0c22763b86b502acaae0532421fa06c3f.png)',
              backgroundSize: 'cover',
              backgroundBlendMode: 'overlay'
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={28} style={{ color: '#DC2626' }} />
              <h2 
                className="text-xl font-semibold"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#DC2626' }}
              >
                OVERSTAY ALERTS
              </h2>
            </div>
            <div className="space-y-3">
              {overstay.bookings.map((booking) => (
                <div
                  key={booking.id}
                  className="flex items-center justify-between p-4 border"
                  style={{ backgroundColor: '#FFFFFF', borderColor: '#DC2626', borderWidth: '2px' }}
                  data-testid={`overstay-item-${booking.id}`}
                >
                  <div>
                    <p 
                      className="font-bold text-lg"
                      style={{ fontFamily: 'JetBrains Mono, monospace', color: '#DC2626' }}
                    >
                      {booking.vehicle_number}
                    </p>
                    <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A', fontSize: '14px' }}>
                      Overstay: {booking.overstay_minutes} minutes
                    </p>
                  </div>
                  <div className="text-right">
                    <p 
                      className="font-bold text-lg"
                      style={{ fontFamily: 'JetBrains Mono, monospace', color: '#DC2626' }}
                    >
                      Penalty: ₹{booking.penalty_amount}
                    </p>
                    <Button
                      onClick={() => handleCheckOut(booking.id)}
                      size="sm"
                      data-testid={`checkout-overstay-${booking.id}`}
                      style={{
                        backgroundColor: '#DC2626',
                        color: '#FFFFFF',
                        fontFamily: 'IBM Plex Sans, sans-serif',
                        marginTop: '8px'
                      }}
                    >
                      Check Out
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Bookings */}
        <div 
          className="p-6 border"
          style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7', borderWidth: '2px' }}
        >
          <h2 
            className="text-xl font-semibold mb-4"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}
          >
            Active Bookings
          </h2>
          {inventory.active_bookings.length === 0 ? (
            <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
              No active bookings
            </p>
          ) : (
            <div className="space-y-3">
              {inventory.active_bookings.map((booking) => (
                <div
                  key={booking.id}
                  className="flex items-center justify-between p-4 border"
                  style={{ borderColor: '#E4E4E7' }}
                  data-testid={`active-booking-${booking.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-12 h-12 flex items-center justify-center"
                      style={{ backgroundColor: '#10B981' }}
                    >
                      <CheckCircle size={24} style={{ color: '#FFFFFF' }} />
                    </div>
                    <div>
                      <p 
                        className="font-bold"
                        style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}
                      >
                        {booking.vehicle_number}
                      </p>
                      <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A', fontSize: '14px' }}>
                        Exit: {new Date(booking.estimated_exit).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleCheckOut(booking.id)}
                    size="sm"
                    variant="outline"
                    data-testid={`checkout-button-${booking.id}`}
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    Check Out
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuardDashboard;
